import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Helper to determine if this app is running as an **installed app**
 * (i.e., standalone; i.e., a PWA), or just running in a browser.
 *
 * @returns true if this is a standalone display
 */
export const isStandaloneDisplay = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

/**
 * Opens a "popout" URL in a new browser window/tab, except on platforms that don't actually
 * support a separate window (e.g. an installed/standalone PWA on Android). There, `window.open`
 * silently reuses the current window but starts a disconnected session history, which breaks the
 * hardware/gesture Back button (it exits the app instead of returning to the previous screen).
 * In that case, fall back to an in-app route change so Back continues to work normally.
 */
export const usePopoutNavigation = () => {
  const navigate = useNavigate();

  return useCallback(
    (url: URL) => {
      if (isStandaloneDisplay()) {
        navigate(`${url.pathname}${url.search}`);
        return;
      }

      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    },
    [navigate]
  );
};
